import styled from '@emotion/styled';

export const MobilePage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: max(14px, env(safe-area-inset-bottom));
  scroll-padding-bottom: calc(96px + env(safe-area-inset-bottom));
  -webkit-overflow-scrolling: touch;

  * {
    box-sizing: border-box;
  }
`;

export const MobileHero = styled.section`
  background:
    radial-gradient(circle at top right, rgba(103, 232, 249, 0.26), transparent 42%),
    linear-gradient(135deg, #0c2d48 0%, #1e40af 52%, #0284c7 100%);
  border-radius: clamp(24px, 7vw, 30px);
  box-shadow: 0 18px 42px rgba(30, 64, 175, 0.22);
  color: #ffffff;
  isolation: isolate;
  overflow: hidden;
  padding: clamp(18px, 5vw, 22px);
  position: relative;

  h2 {
    font-size: clamp(1.32rem, 7.5vw, 1.9rem);
    line-height: 1.08;
    margin: 10px 0 8px;
  }

  p {
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.88rem;
    line-height: 1.5;
    margin: 0;
  }
`;

export const HeroPill = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 850;
  gap: 7px;
  padding: 6px 10px;

  &::before {
    background: #67e8f9;
    border-radius: 50%;
    content: '';
    height: 8px;
    width: 8px;
  }
`;

export const Card = styled.section`
  background: #ffffff;
  border: 1px solid #e8f0fe;
  border-radius: 24px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.07);
  padding: 15px;
`;

export const SectionHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;

  span {
    color: #64748b;
    display: block;
    font-size: 0.68rem;
    font-weight: 900;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  h3 {
    color: #0f172a;
    font-size: 1.03rem;
    margin: 3px 0 0;
  }
`;

export const Button = styled.button`
  align-items: center;
  background: ${p => (p.$secondary ? '#f8fafc' : 'linear-gradient(135deg, #0284c7, #1e40af)')};
  border: 1px solid ${p => (p.$secondary ? '#e2e8f0' : 'transparent')};
  border-radius: 16px;
  color: ${p => (p.$secondary ? '#334155' : '#ffffff')};
  display: inline-flex;
  font-weight: 900;
  gap: 8px;
  justify-content: center;
  min-height: 48px;
  padding: 0 14px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  width: ${p => (p.$full ? '100%' : 'auto')};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

export const Field = styled.label`
  color: #334155;
  display: flex;
  flex-direction: column;
  font-size: 0.76rem;
  font-weight: 850;
  gap: 7px;
`;

const control = `
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  color: #0f172a;
  font-size: 16px;
  min-height: 48px;
  outline: none;
  padding: 0 13px;
  -webkit-appearance: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  width: 100%;
`;

export const Input = styled.input`${control}`;
export const Select = styled.select`${control}`;
export const TextArea = styled.textarea`
  ${control}
  min-height: 112px;
  padding-bottom: 12px;
  padding-top: 12px;
  resize: vertical;
`;

export const Grid2 = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 360px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.article`
  background: #ffffff;
  border: 1px solid #e8f0fe;
  border-radius: 22px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  min-height: 112px;
  padding: 15px;
  position: relative;

  &::before {
    background: ${p => p.$accent || '#0284c7'};
    border-radius: 99px;
    content: '';
    height: 5px;
    left: 15px;
    position: absolute;
    right: 15px;
    top: 0;
  }

  span {
    color: #64748b;
    display: block;
    font-size: 0.74rem;
    font-weight: 850;
    margin-top: 5px;
  }

  strong {
    color: #0f172a;
    display: block;
    font-size: clamp(1.25rem, 7vw, 1.68rem);
    line-height: 1.12;
    margin-top: 12px;
    overflow-wrap: anywhere;
  }

  small {
    color: #94a3b8;
    display: block;
    font-size: 0.7rem;
    font-weight: 750;
    margin-top: 6px;
  }
`;

export const EmptyState = styled.div`
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 20px;
  padding: 18px;
  text-align: center;

  strong { color: #0f172a; display: block; }
  p { color: #64748b; font-size: 0.82rem; line-height: 1.45; margin: 6px 0 0; }
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

export const ListItem = styled.button`
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  color: inherit;
  display: flex;
  gap: 11px;
  min-height: 72px;
  padding: 12px;
  text-align: left;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  width: 100%;
`;

export const Badge = styled.span`
  background: ${p => ({
    high: '#fee2e2', critical: '#ffe4e6', medium: '#fef3c7', low: '#dcfce7', paid: '#dcfce7', completed: '#e0f2fe', pending: '#fef3c7'
  }[p.$tone] || '#e0f2fe')};
  border-radius: 999px;
  color: ${p => ({
    high: '#b91c1c', critical: '#be123c', medium: '#92400e', low: '#166534', paid: '#166534', completed: '#075985', pending: '#92400e'
  }[p.$tone] || '#075985')};
  display: inline-flex;
  font-size: 0.66rem;
  font-weight: 950;
  padding: 6px 9px;
  text-transform: capitalize;
`;

export const Skeleton = styled.div`
  background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 37%, #eef2f7 63%);
  background-size: 400% 100%;
  border-radius: ${p => p.$radius || '24px'};
  height: ${p => p.$height || '140px'};
`;


export const SegmentedControl = styled.div`
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.06);
  display: grid;
  gap: 4px;
  padding: 4px;

  button {
    background: transparent;
    border: 0;
    border-radius: 16px;
    color: #64748b;
    font-size: 0.78rem;
    font-weight: 900;
    min-height: 44px;
    padding: 0 10px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  button.active {
    background: linear-gradient(135deg, #0284c7, #1e40af);
    color: #ffffff;
    box-shadow: 0 8px 18px rgba(2, 132, 199, 0.2);
  }
`;

export const SheetBackdrop = styled.div`
  background: rgba(15, 23, 42, 0.38);
  inset: 0;
  padding-top: env(safe-area-inset-top);
  position: fixed;
  z-index: 90;
`;

export const BottomSheet = styled.section`
  background: #ffffff;
  border-radius: 30px 30px 0 0;
  bottom: 0;
  box-shadow: 0 -22px 48px rgba(15, 23, 42, 0.2);
  left: 0;
  max-height: calc(88vh - env(safe-area-inset-top));
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 10px 16px calc(22px + env(safe-area-inset-bottom));
  position: fixed;
  right: 0;
  -webkit-overflow-scrolling: touch;
`;

export const SheetHandle = styled.div`
  background: #cbd5e1;
  border-radius: 99px;
  height: 5px;
  margin: 0 auto 15px;
  width: 46px;
`;

export const StickyActionBar = styled.div`
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  bottom: calc(86px + env(safe-area-inset-bottom));
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.16);
  display: flex;
  gap: 8px;
  left: 14px;
  padding: 8px;
  position: sticky;
  right: 14px;
  z-index: 20;

  button {
    flex: 1;
  }
`;
